import { useEffect, useMemo, useState } from "react";
import { Layers, Radar } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { api } from "@/lib/api";
import { ARCHETYPE_LABELS } from "@/constants/content";

const Badge = ({ kind }) => (kind === "detected"
  ? <span className="rounded-full bg-emerald-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-300">Detected</span>
  : <span className="rounded-full border border-dashed border-cyan-400/50 px-1.5 py-0.5 text-[9px] font-bold uppercase text-cyan-300">Live guess</span>);

const CriterionItem = ({ c }) => (
  <AccordionItem value={c.key} className="border-white/[0.06]" data-testid={`inspector-criterion-${c.key}`}>
    <AccordionTrigger className="gap-3 py-3 text-left text-sm font-semibold text-white hover:no-underline">
      <span className="flex min-w-0 flex-1 items-center gap-3">
        <span className="min-w-0 flex-1">{c.name}</span>
        <span className="shrink-0 font-mono text-xs text-cyan-300" data-testid={`inspector-criterion-weight-${c.key}`}>{c.weight} pts</span>
      </span>
    </AccordionTrigger>
    <AccordionContent className="space-y-2 text-xs leading-relaxed text-slate-400">
      <p>{c.description}</p>
      <div className="h-1.5 overflow-hidden rounded-full bg-white/5" aria-hidden="true">
        <div className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500" style={{ width: `${(c.weight / 8) * 100}%` }} />
      </div>
      {c.rubric && (
        <ul className="grid gap-1 sm:grid-cols-3">
          {["0", "5", "10"].map((k) => c.rubric[k] && <li key={k} className="rounded-lg bg-white/[0.03] p-2"><b className="font-mono text-slate-200">{k}</b> — {c.rubric[k]}</li>)}
        </ul>
      )}
      {c.fatal_flaw_rule && <p className="text-rose-300">Fatal flaw if score &lt; {c.fatal_flaw_rule.threshold}: {c.fatal_flaw_rule.message}</p>}
    </AccordionContent>
  </AccordionItem>
);

export const CriteriaInspector = ({ detected, liveGuess }) => {
  const [archetypes, setArchetypes] = useState([]);
  const [criteria, setCriteria] = useState([]);
  const [picked, setPicked] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/taxonomy/archetypes"), api.get("/criteria", { params: { group: "overlay" } })])
      .then(([a, c]) => { setArchetypes([...a.data].sort((x, y) => x.number - y.number)); setCriteria(c.data); })
      .catch(() => setFailed(true));
  }, []);
  useEffect(() => { setPicked(detected || null); }, [detected]);
  useEffect(() => { if (!detected) setPicked(null); }, [liveGuess, detected]);

  const active = picked || detected || liveGuess || archetypes[0]?.key;
  const list = useMemo(() => criteria.filter((c) => c.archetype_key === active), [criteria, active]);

  return (
    <aside className="iv-glass rounded-2xl p-4 sm:p-5" aria-labelledby="inspector-title" data-testid="criteria-inspector">
      <h2 id="inspector-title" className="flex items-center gap-2 text-base font-bold text-white md:text-lg">
        <Radar className="h-4 w-4 text-cyan-300" aria-hidden="true" /> 8-Industry Specialist Criteria Inspector
      </h2>
      <p className="mt-1 text-xs text-slate-400">Every idea gets 12 universal criteria + the 8 overlay criteria of its industry (50 pts, normalised with the universal 100).</p>
      {failed && <p className="mt-3 text-xs text-rose-300" role="alert">Couldn't load the criteria library. Please refresh.</p>}
      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2" role="tablist" aria-label="Industry archetypes">
        {archetypes.map((a) => {
          const isActive = a.key === active;
          const kind = a.key === detected ? "detected" : !detected && a.key === liveGuess ? "guess" : null;
          return (
            <button key={a.key} type="button" role="tab" aria-selected={isActive} onClick={() => setPicked(a.key)}
              className={`flex min-w-0 items-start gap-2 rounded-xl border px-3 py-2 text-left text-xs font-semibold transition-[border-color,background-color,box-shadow] ${
                isActive ? "border-cyan-400/60 bg-cyan-500/10 text-white shadow-[0_0_20px_rgba(6,182,212,0.15)]" : "border-white/10 text-slate-300 hover:border-white/25"
              } ${a.key === detected ? "!border-emerald-400/60" : ""}`}
              data-testid={`inspector-archetype-${a.key}`} data-detected={a.key === detected ? "true" : "false"}>
              <span className="font-mono text-slate-500">{a.number}</span>
              <span className="min-w-0 flex-1 break-words">{ARCHETYPE_LABELS[a.key] || a.name}</span>
              {kind && <Badge kind={kind} />}
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex items-center gap-2 text-xs font-semibold text-slate-300" data-testid="inspector-active-archetype">
        <Layers className="h-3.5 w-3.5 text-violet-300" aria-hidden="true" />
        {ARCHETYPE_LABELS[active]} · {list.length} criteria · {list.reduce((s, c) => s + c.weight, 0)} pts
      </div>
      <Accordion type="single" collapsible className="mt-1">
        {list.map((c) => <CriterionItem key={c.key} c={c} />)}
      </Accordion>
    </aside>
  );
};
